import React, { useState } from "react";
import {
  DndContext,
  DragOverlay,
  closestCorners,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragStartEvent,
  DragEndEvent,
  DragOverEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Deal, DealStage, PIPELINE_STAGES } from "@/hooks/useDealPipeline";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { DollarSign, Calendar, GripVertical } from "lucide-react";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import { ScrollArea } from "@/components/ui/scroll-area";

interface KanbanBoardProps {
  deals: Deal[];
  onDealMove: (dealId: string, newStage: DealStage) => Promise<void>;
  onDealSelect: (deal: Deal) => void;
  selectedDealId?: string;
}

interface KanbanColumnProps {
  stage: { stage: DealStage; label: string };
  deals: Deal[];
  onDealSelect: (deal: Deal) => void;
  selectedDealId?: string;
}

interface DealCardProps {
  deal: Deal;
  onSelect: () => void;
  isSelected: boolean;
  isDragging?: boolean;
}

// Sortable Deal Card
function SortableDealCard({ deal, onSelect, isSelected }: DealCardProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: deal.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        "touch-none",
        isDragging && "opacity-50"
      )}
    >
      <DealCard
        deal={deal}
        onSelect={onSelect}
        isSelected={isSelected}
        isDragging={isDragging}
      />
      <div
        {...attributes}
        {...listeners}
        className="absolute top-2 right-2 cursor-grab active:cursor-grabbing p-1 rounded hover:bg-muted/50"
      >
        <GripVertical className="h-4 w-4 text-muted-foreground" />
      </div>
    </div>
  );
}

// Deal Card Component
function DealCard({ deal, onSelect, isSelected, isDragging }: DealCardProps) {
  const formatCurrency = (amount: number | undefined, currency: string) => {
    if (!amount) return "—";
    return new Intl.NumberFormat('en-CA', { style: 'currency', currency }).format(amount);
  };

  return (
    <Card
      className={cn(
        "cursor-pointer transition-all hover:shadow-md relative",
        isSelected && "ring-2 ring-primary",
        isDragging && "shadow-lg rotate-2"
      )}
      onClick={onSelect}
    >
      <CardContent className="p-3">
        <h4 className="font-medium text-sm truncate pr-6">{deal.business_name}</h4>
        <p className="text-xs text-muted-foreground truncate mt-1">
          {deal.business_description || "No description"}
        </p>
        <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground">
          <span className="flex items-center gap-1">
            <DollarSign className="h-3 w-3" />
            {formatCurrency(deal.asking_price, deal.currency)}
          </span>
          <span className="flex items-center gap-1">
            <Calendar className="h-3 w-3" />
            {format(new Date(deal.created_at), 'MMM d')}
          </span>
        </div>
      </CardContent>
    </Card>
  );
}

// Kanban Column Component
function KanbanColumn({ stage, deals, onDealSelect, selectedDealId }: KanbanColumnProps) {
  const stageDeals = deals.filter(d => d.current_stage === stage.stage);
  
  const getColumnColor = (stageName: string) => {
    if (stageName.includes('signed') || stageName === 'closing') return "bg-green-500/10 border-green-500/30";
    if (stageName.includes('pending')) return "bg-yellow-500/10 border-yellow-500/30";
    if (stageName === 'submission') return "bg-blue-500/10 border-blue-500/30";
    return "bg-muted/30 border-border";
  };

  return (
    <div className={cn(
      "flex-shrink-0 w-72 rounded-lg border p-3",
      getColumnColor(stage.stage)
    )}>
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-medium text-sm">{stage.label}</h3>
        <Badge variant="secondary" className="text-xs">
          {stageDeals.length}
        </Badge>
      </div>
      
      <SortableContext
        items={stageDeals.map(d => d.id)}
        strategy={verticalListSortingStrategy}
      >
        <ScrollArea className="h-[500px]">
          <div className="space-y-2 pr-2">
            {stageDeals.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground text-xs">
                Drop deals here
              </div>
            ) : (
              stageDeals.map(deal => (
                <SortableDealCard
                  key={deal.id}
                  deal={deal}
                  onSelect={() => onDealSelect(deal)}
                  isSelected={selectedDealId === deal.id}
                />
              ))
            )}
          </div>
        </ScrollArea>
      </SortableContext>
    </div>
  );
}

// Main Kanban Board Component
export function KanbanBoard({ deals, onDealMove, onDealSelect, selectedDealId }: KanbanBoardProps) {
  const [activeDeal, setActiveDeal] = useState<Deal | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragStart = (event: DragStartEvent) => {
    const { active } = event;
    const deal = deals.find(d => d.id === active.id);
    if (deal) setActiveDeal(deal);
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveDeal(null);

    if (!over) return;

    const activeDealId = active.id as string;
    const deal = deals.find(d => d.id === activeDealId);
    if (!deal) return;

    // Find the target stage from the over element
    // The over element could be a deal card or the column itself
    const overDeal = deals.find(d => d.id === over.id);
    
    if (overDeal && overDeal.current_stage !== deal.current_stage) {
      await onDealMove(activeDealId, overDeal.current_stage);
    }
  };

  const handleDragOver = (event: DragOverEvent) => {
    const { active, over } = event;
    
    if (!over) return;

    const activeDealId = active.id as string;
    const overDealId = over.id as string;

    const activeDeal = deals.find(d => d.id === activeDealId);
    const overDeal = deals.find(d => d.id === overDealId);

    if (!activeDeal || !overDeal) return;
    if (activeDeal.current_stage === overDeal.current_stage) return;

    // Visual feedback is handled by DndContext
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCorners}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragOver={handleDragOver}
    >
      <div className="flex gap-4 overflow-x-auto pb-4">
        {PIPELINE_STAGES.map(stage => (
          <KanbanColumn
            key={stage.stage}
            stage={stage}
            deals={deals}
            onDealSelect={onDealSelect}
            selectedDealId={selectedDealId}
          />
        ))}
      </div>

      <DragOverlay>
        {activeDeal ? (
          <div className="w-72">
            <DealCard
              deal={activeDeal}
              onSelect={() => {}}
              isSelected={false}
              isDragging
            />
          </div>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
}
